<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .ticket-detail-page
    //- ── Skeleton Loading ──────────────────────────────────────────
    template(v-if="loading && !sticker")
      .skeleton-banner
      .skeleton-body
        //- Creator row skeleton
        .skeleton-creator-row
          .skeleton-circle
          .skeleton-bar.skeleton-bar--short

        //- Title skeleton
        .skeleton-bar.skeleton-bar--title
        .skeleton-bar.skeleton-bar--medium

        //- Meta skeleton (date + location)
        .skeleton-meta-row
          .skeleton-bar.skeleton-bar--meta
        .skeleton-meta-row
          .skeleton-bar.skeleton-bar--meta

        //- Action buttons skeleton
        .skeleton-actions
          .skeleton-circle.skeleton-circle--sm
          .skeleton-circle.skeleton-circle--sm
          .skeleton-circle.skeleton-circle--sm

        .skeleton-divider

        //- Order summary skeleton
        .skeleton-bar.skeleton-bar--section-title
        .skeleton-summary-row
          .skeleton-bar.skeleton-bar--short
          .skeleton-bar.skeleton-bar--xs
        .skeleton-summary-row
          .skeleton-bar.skeleton-bar--short
          .skeleton-bar.skeleton-bar--xs
        .skeleton-divider

        //- CTA buttons skeleton
        .skeleton-cta-row
          .skeleton-btn
          .skeleton-btn

        .skeleton-divider

        //- Details skeleton
        .skeleton-bar.skeleton-bar--section-title
        .skeleton-bar.skeleton-bar--full
        .skeleton-bar.skeleton-bar--full
        .skeleton-bar.skeleton-bar--medium

    //- ── Main Content ──────────────────────────────────────────────
    template(v-else-if="sticker")
      //- Landscape event banner (dedicated banner image only)
      .event-banner(v-if="hasEventBanner")
        img(:src="sticker.bannerUrl" :alt="sticker.title").banner-img

        transition(name="toast-fade")
          .confirmation-toast(v-if="confirmationSent")
            span Confirmation Email Sent

        .banner-overlay-info
          .overlay-content
            .date-time-info(v-if="sticker.eventDate")
              span.date-text {{ formatBannerDate(sticker.eventDate) }}
              span.separator •
              span.time-text {{ formatBannerTime(sticker.eventDate) }}
            .location-info(v-if="sticker.location && !sticker.hideLocation")
              span.location-text {{ sticker.location }}

      //- Sticker hero — full artwork visible when no event banner
      .sticker-hero(v-else-if="sticker.url")
        transition(name="toast-fade")
          .confirmation-toast(v-if="confirmationSent")
            span Confirmation Email Sent
        .sticker-hero-inner
          img(:src="sticker.url" :alt="sticker.title").sticker-hero-img

      //- Content Body
      .content-body
        //- Campaign Type Badge
        .campaign-badge
          .campaign-badge-dot
          span.campaign-badge-text TICKET

        //- Creator Info
        .creator-info(
          :class="{ 'is-clickable': creatorProfilePath }"
          @click="handleCreatorClick"
        )
          .creator-avatar
            img(
              v-if="creatorAvatar"
              :src="creatorAvatar"
              :alt="creatorName"
            )
            span.creator-initial(v-else) {{ creatorInitial }}
          .creator-details
            .creator-name {{ creatorName }}
            .creator-verified Verified Creator

        //- Title
        h2.sticker-title {{ sticker.title }}

        //- Event Date + Location + Action Buttons Row
        .event-meta-block
          .event-meta-left
            .event-date-row(v-if="sticker.eventDate")
              svg.meta-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                rect(x="3" y="4" width="18" height="18" rx="2" ry="2")
                line(x1="16" y1="2" x2="16" y2="6")
                line(x1="8" y1="2" x2="8" y2="6")
                line(x1="3" y1="10" x2="21" y2="10")
              span.event-date-text {{ formatEventDateTime(sticker.eventDate) }}

            .event-location-row(v-if="sticker.location && !sticker.hideLocation")
              svg.meta-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                path(d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z")
                circle(cx="12" cy="10" r="3")
              a(
                v-if="sticker.locationType !== 'virtual'"
                :href="`https://maps.google.com/?q=${encodeURIComponent(sticker.location)}`"
                target="_blank"
                rel="noopener noreferrer"
              ).location-text.location-link {{ sticker.location }}
              span(v-else).location-text {{ sticker.location }}
              .location-badge(:class="sticker.locationType === 'virtual' ? 'badge-virtual' : 'badge-physical'")
                | {{ sticker.locationType === 'virtual' ? 'VIRTUAL' : 'PHYSICAL' }}

            .event-location-row(v-else-if="sticker.hideLocation")
              svg.meta-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                path(d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z")
                circle(cx="12" cy="10" r="3")
              span.location-text.location-undisclosed Undisclosed — View in Announcements

          //- Action Icon Buttons (bell / share / calendar)
          .event-action-buttons
            button(@click="showAnnouncements = true" type="button" title="Announcements").action-icon-btn
              img(src="@/assets/icons/bell.svg" alt="Announcements").action-icon
            button(@click="showShare = true" type="button" title="Share").action-icon-btn
              img(src="@/assets/icons/share.svg" alt="Share").action-icon
            button(@click="showCalendar = true" type="button" title="Add to Calendar").action-icon-btn
              img(src="@/assets/icons/calendar.svg" alt="Calendar").action-icon

        //- Order Summary
        .detail-section(v-if="tickets.length")
          h3.section-heading Order Summary
          .summary-lines
            .summary-line(v-for="tier in orderSummary" :key="tier.tierName")
              span.summary-tier {{ tier.tierName }} × {{ tier.count }}
              span.summary-price(v-if="tier.unitCost") ${{ (tier.unitCost * tier.count).toFixed(2) }}
            .summary-divider
            .summary-total-row
              span.summary-total-label Total
              span.summary-total-value ${{ totalCost }}

        //- CTA Buttons
        .cta-section
          button(@click="showTicketViewer = true" type="button").btn-primary
            svg.btn-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
              path(d="M2 9a3 3 0 1 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 1 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z")
              path(d="M13 5v2")
              path(d="M13 17v2")
              path(d="M13 11v2")
            span View Tickets
          button(
            @click="handleResendConfirmation"
            type="button"
            :disabled="resendLoading"
          ).btn-secondary
            span(v-if="!resendLoading") Resend Confirmation
            span(v-else) Sending...

        //- Description / Details
        .detail-section(v-if="sticker.description")
          h3.section-heading Details
          .section-description(v-html="sticker.description")

        //- Sticker Inventory
        .detail-section
          h3.section-heading Sticker Inventory
          .sticker-inventory-card
            .inventory-thumb
              img(:src="sticker.url" :alt="sticker.title").inventory-thumb-img
            .inventory-info
              h4.inventory-title {{ sticker.title }}
              p.inventory-owned You own: {{ tickets.length }} ticket{{ tickets.length !== 1 ? 's' : '' }}
            button(@click="goToStickerPage" type="button").btn-outline Buy Another

        //- Purchase Date
        .detail-section(v-if="tickets.length")
          p.purchase-date-text Purchased on {{ formatPurchaseDate(tickets[0]?.createdAt) }}

    //- ── Error State ───────────────────────────────────────────────
    .error-state(v-else-if="!loading")
      p.error-text Ticket details not found.

    //- ── Ticket Viewer Dialog ─────────────────────────────────────
    el-dialog(
      v-model="showTicketViewer"
      :show-close="false"
      :append-to-body="true"
      :z-index="99999"
      :lock-scroll="true"
      width="400px"
      custom-class="ticket-viewer-dialog"
      modal-class="ticket-viewer-overlay"
    )
      .ticket-viewer-wrap
        //- Loading state
        .tv-loading(v-if="ticketsLoading")
          .tv-spinner
          p.tv-loading-text Loading your tickets...

        //- Tickets carousel
        template(v-else-if="tickets.length")
          .tv-carousel-area
            .tv-ticket-stage
              //- Ticket cards
              .tv-ticket-card(
                v-for="(ticket, index) in tickets"
                :key="ticket.id"
                :class="{ 'tv-ticket--active': index === currentTicketIndex, 'tv-ticket--hidden': index !== currentTicketIndex }"
              )
                //- Event image with shimmer
                .tv-card-image(:class="{ 'tv-card-image--sticker': !sticker.bannerUrl }")
                  .tv-shimmer-overlay
                  img(
                    v-if="sticker.bannerUrl || sticker.url"
                    :src="sticker.bannerUrl || sticker.url"
                    :alt="sticker.title"
                  ).tv-card-img
                  button(@click="showTicketViewer = false" type="button").tv-close-btn
                    svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round")
                      polyline(points="15 18 9 12 15 6")

                //- Ticket body
                .tv-card-body
                  h3.tv-event-name {{ sticker.title }}

                  //- Date & Time grid
                  .tv-info-grid
                    .tv-info-cell
                      .tv-info-label Date
                      .tv-info-value {{ formatTicketDate(sticker.eventDate) }}
                    .tv-info-cell
                      .tv-info-label Time
                      .tv-info-value {{ formatTicketTime(sticker.eventDate) }}

                  //- Location
                  .tv-info-row(v-if="sticker.location && !sticker.hideLocation")
                    .tv-info-label Location
                    .tv-info-value {{ sticker.location }}

                  //- Tier / option
                  .tv-info-row
                    .tv-info-label Ticket Type
                    .tv-info-value {{ getTicketTierName(ticket) }}
                    span.tv-option-text(v-if="ticket.selectedOption") &nbsp;· {{ ticket.selectedOption }}

                  //- Seat info
                  .tv-seat-grid(v-if="ticket.section || ticket.row || ticket.seat")
                    .tv-seat-cell(v-if="ticket.section")
                      .tv-seat-value {{ ticket.section }}
                      .tv-seat-label SEC
                    .tv-seat-cell(v-if="ticket.row")
                      .tv-seat-value {{ ticket.row }}
                      .tv-seat-label ROW
                    .tv-seat-cell(v-if="ticket.seat")
                      .tv-seat-value {{ ticket.seat }}
                      .tv-seat-label SEAT

                  //- QR Code — encodes a URL to this ticket's detail page
                  .tv-qr-wrap
                    qrcode-vue(:value="ticketQrUrl(ticket.id)" :size="160" level="H" render-as="svg")

                  //- Check-in status
                  .tv-checked-in(v-if="ticket.checkedInAt")
                    svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
                      polyline(points="20 6 9 17 4 12")
                    span Checked in {{ formatDateTime(ticket.checkedInAt) }}
                  .tv-not-checked-in(v-else)
                    span Not yet checked in

            //- Dots navigation
            .tv-dots(v-if="tickets.length > 1")
              button(
                v-for="(_, i) in tickets"
                :key="i"
                @click="currentTicketIndex = i"
                type="button"
                :class="['tv-dot', { 'tv-dot--active': i === currentTicketIndex }]"
              )

        //- Empty state
        .tv-empty(v-else)
          .tv-empty-icon 🎟
          p.tv-empty-text No tickets found for this event.

    //- ── Dialogs ──────────────────────────────────────────────────

    //- Share Dialog
    el-dialog(
      v-model="showShare"
      title="Share"
      :show-close="true"
      width="400px"
    )
      .dialog-option(@click="copyUrlToClipboard")
        .dialog-option-icon
          svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
            rect(x="9" y="9" width="13" height="13" rx="2" ry="2")
            path(d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1")
        span.dialog-option-text Copy URL
      .dialog-option(@click="handleShareTo")
        .dialog-option-icon
          img(src="@/assets/icons/share.svg" alt="Share")
        span.dialog-option-text Share To
      .dialog-option(v-if="sticker && sticker.organization && sticker.organization.email" @click="contactOrganizer")
        .dialog-option-icon
          svg(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round")
            path(d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z")
        span.dialog-option-text Contact Organizer
      .dialog-note-section
        p.dialog-note-text Share this event with friends and family.

    //- Calendar Dialog
    el-dialog(
      v-model="showCalendar"
      title="Add to Calendar"
      :show-close="true"
      width="400px"
    )
      .dialog-option(@click="addToGoogleCalendar")
        .dialog-option-icon
          img(src="@/assets/icons/calendar.svg" alt="Google Calendar")
        span.dialog-option-text Add to Google Calendar
      .dialog-option(@click="addToAppleCalendar")
        .dialog-option-icon
          img(src="@/assets/icons/calendar.svg" alt="Apple Calendar")
        span.dialog-option-text Add to Apple Calendar
      .dialog-option(@click="addToOutlookCalendar")
        .dialog-option-icon
          img(src="@/assets/icons/calendar.svg" alt="Outlook")
        span.dialog-option-text Add to Outlook Calendar
      .dialog-note-section
        p.dialog-note-text Save this event to your calendar so you never miss it.

    //- Announcements Dialog
    el-dialog(
      v-model="showAnnouncements"
      title="Event Announcements"
      :show-close="true"
      width="500px"
    )
      .announcements-loading(v-if="announcementsLoading")
        .spinner
        p.loading-text Loading announcements...
      .announcements-list(v-else-if="announcements.length")
        .announcement-item(v-for="ann in announcements" :key="ann.id")
          .announcement-header
            span.announcement-author {{ ann.sentBy || 'Event Organizer' }}
            span.announcement-time {{ formatRelativeTime(ann.sentAt || ann.createdAt) }}
          p.announcement-body {{ ann.body }}
      .announcements-empty(v-else)
        p.empty-text No announcements yet. Check back later.
      .dialog-note-section
        p.dialog-note-text You will receive notifications when the organizer posts new announcements.
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ElMessage } from 'element-plus';
import webAppToast from '@/utils/webAppToast';
import QrcodeVue from 'qrcode.vue';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import { makeRequest } from '@/graphs/index';
import {
  findMyTickets,
  findStickerAnnouncements,
  findSticker,
  resendTicketConfirmation,
  trackStickerShare,
} from '@/graphs/gql';
import { addToCalendarClick } from '@/utils/calendarUtils';
import {
  buildVariantTitleMapFromSticker,
  resolveTicketTierName,
} from '@/utils/ticketDisplay';
import { getCreatorProfilePath } from '@/utils/profileNavigation';
import {
  buildStickerShareUrl,
  pickStickerAttributionQuery,
} from '@/utils/stickerShareUrl';

dayjs.extend(utc);
dayjs.extend(relativeTime);

interface IssuedTicket {
  id: string;
  code: string;
  tierName: string;
  selectedOption?: string;
  stickerId: string;
  variantId: string;
  section?: string;
  row?: string;
  seat?: string;
  checkedInAt?: string;
  createdAt: string;
}

interface Announcement {
  id: string;
  stickerId: string;
  subject: string;
  body: string;
  sentBy?: string;
  sentAt?: string;
  createdAt: string;
}

interface TierSummary {
  tierName: string;
  count: number;
  unitCost: number;
}

export default defineComponent({
  name: 'TicketDetailPage',
  components: { WebAppLayout, QrcodeVue },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const me = computed(() => store.getters['users/me']);
    const stickerId = String(route.params.stickerId);

    const loading = ref(false);
    const sticker = ref<any>(null);
    const tickets = ref<IssuedTicket[]>([]);
    const announcements = ref<Announcement[]>([]);
    const announcementsLoading = ref(false);
    const resendLoading = ref(false);
    const confirmationSent = ref(false);

    const showShare = ref(false);
    const showCalendar = ref(false);
    const showAnnouncementsRef = ref(false);
    const showTicketViewer = ref(false);
    const currentTicketIndex = ref(0);
    const ticketsLoading = ref(false);

    const showAnnouncements = computed({
      get: () => showAnnouncementsRef.value,
      set: (val) => {
        showAnnouncementsRef.value = val;
        if (val && !announcements.value.length) loadAnnouncements();
      },
    });

    const creatorName = computed(() => {
      if (sticker.value?.organization?.name) return sticker.value.organization.name;
      if (sticker.value?.user) {
        const { firstname, lastname } = sticker.value.user;
        return [firstname, lastname].filter(Boolean).join(' ') || 'Creator';
      }
      return 'Creator';
    });

    const creatorAvatar = computed(() => (
      sticker.value?.organization?.avatar || sticker.value?.user?.avatar || null
    ));

    const creatorInitial = computed(() => creatorName.value?.charAt(0).toUpperCase() || '?');

    const hasEventBanner = computed(() => !!sticker.value?.bannerUrl);

    const creatorProfilePath = computed(() => getCreatorProfilePath(
      sticker.value?.entity,
      sticker.value?.organization,
      sticker.value?.user,
    ));

    const handleCreatorClick = () => {
      if (creatorProfilePath.value) {
        router.push(creatorProfilePath.value);
      }
    };

    const variantTitleMap = computed(() => (
      buildVariantTitleMapFromSticker(sticker.value?.varients)
    ));

    function getTicketTierName(ticket: IssuedTicket) {
      return resolveTicketTierName(ticket, variantTitleMap.value);
    }

    const orderSummary = computed((): TierSummary[] => {
      const variantCosts: Record<string, number> = {};
      sticker.value?.varients?.forEach((v: any) => {
        if (v?.id && v?.cost != null) variantCosts[v.id] = Number(v.cost);
      });

      const map: Record<string, TierSummary> = {};
      tickets.value.forEach((t) => {
        const tierName = getTicketTierName(t);
        const key = t.variantId || tierName;
        if (!map[key]) {
          map[key] = { tierName, count: 0, unitCost: variantCosts[t.variantId] || 0 };
        }
        map[key].count += 1;
      });
      return Object.values(map);
    });

    const totalCost = computed(() => {
      const total = orderSummary.value.reduce(
        (sum, tier) => sum + tier.unitCost * tier.count,
        0,
      );
      return total.toFixed(2);
    });

    async function loadSticker() {
      loading.value = true;
      try {
        const token = me.value?.token ?? null;
        const response = await makeRequest(
          'query',
          findSticker,
          { id: stickerId },
          token ? { token } : null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        sticker.value = response?.data?.findSticker || null;
      } catch (err) {
        console.error('Failed to load sticker:', err);
      } finally {
        loading.value = false;
      }
    }

    async function loadTickets() {
      try {
        const token = me.value?.token ?? null;
        const response = await makeRequest(
          'query',
          findMyTickets,
          { stickerId },
          token ? { token } : null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        tickets.value = response?.data?.findMyTickets || [];
      } catch (err) {
        console.error('Failed to load tickets:', err);
      }
    }

    async function loadAnnouncements() {
      announcementsLoading.value = true;
      try {
        const token = me.value?.token ?? null;
        const response = await makeRequest(
          'query',
          findStickerAnnouncements,
          { stickerId },
          token ? { token } : null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        announcements.value = response?.data?.findStickerAnnouncements || [];
      } catch (err) {
        console.error('Failed to load announcements:', err);
      } finally {
        announcementsLoading.value = false;
      }
    }

    onMounted(async () => {
      await Promise.all([loadSticker(), loadTickets()]);
    });

    function formatEventDateTime(date: string) {
      return dayjs(date).format('dddd MMMM Do • h:mm A');
    }

    function formatBannerDate(date: string) {
      return dayjs(date).format('MM/DD/YY');
    }

    function formatBannerTime(date: string) {
      return dayjs(date).format('hh:mm a');
    }

    function formatPurchaseDate(date: string) {
      return dayjs(date).format('M/D/YY [at] h:mm a');
    }

    function formatRelativeTime(date: string) {
      return dayjs(date).fromNow();
    }

    function ticketQrUrl(ticketId: string) {
      return `${window.location.origin}/web/ticket/${stickerId}/detail?ticketId=${ticketId}`;
    }

    function formatTicketDate(date: string) {
      if (!date) return '—';
      return dayjs(date).format('MMMM Do, YYYY');
    }

    function formatTicketTime(date: string) {
      if (!date) return '—';
      return dayjs(date).format('hh:mm A');
    }

    function formatDateTime(date: string) {
      return dayjs(date).format('MMM D, YYYY h:mm A');
    }

    function goToViewTickets() {
      router.push(`/web/tickets/${stickerId}`);
    }

    function goToStickerPage() {
      router.push(`/web/sticker/${stickerId}`);
    }

    async function handleResendConfirmation() {
      resendLoading.value = true;
      try {
        const token = me.value?.token ?? null;
        const orgId = sticker.value?.organization?.id ?? null;
        const response = await makeRequest(
          'mutation',
          resendTicketConfirmation,
          { stickerId, ...(orgId ? { orgId } : {}) },
          token ? { token } : null,
        );
        if (response?.errors) throw new Error(response.errors[0]?.message);
        confirmationSent.value = true;
        webAppToast.success('Confirmation email sent');
        setTimeout(() => { confirmationSent.value = false; }, 3000);
      } catch (err) {
        console.error('Failed to resend confirmation:', err);
        ElMessage.error('Failed to resend confirmation email');
      } finally {
        resendLoading.value = false;
      }
    }

    async function trackStickerShareComplete() {
      if (!stickerId) return;

      try {
        await makeRequest(
          'mutation',
          trackStickerShare,
          { stickerId },
          {},
        );
      } catch (err) {
        console.error('Failed to track sticker share:', err);
      }
    }

    function getShareUrl() {
      const slugOrId = sticker.value?.slug || stickerId;
      return buildStickerShareUrl(slugOrId, pickStickerAttributionQuery(route.query));
    }

    async function copyUrlToClipboard() {
      const url = getShareUrl();
      try {
        await navigator.clipboard.writeText(url);
        webAppToast.success('URL copied to clipboard');
        await trackStickerShareComplete();
        showShare.value = false;
      } catch {
        ElMessage.error('Failed to copy URL');
      }
    }

    async function handleShareTo() {
      const url = getShareUrl();
      if (navigator.share) {
        try {
          await navigator.share({ title: sticker.value?.title || 'Ticket', url });
          await trackStickerShareComplete();
        } catch (err: any) {
          if (err?.name !== 'AbortError') {
            await copyUrlToClipboard();
            return;
          }
        }
      } else {
        await copyUrlToClipboard();
        return;
      }
      showShare.value = false;
    }

    function contactOrganizer() {
      const email = sticker.value?.organization?.email;
      if (!email) return;
      showShare.value = false;
      window.location.href = `mailto:${email}?subject=${encodeURIComponent(`Question about: ${sticker.value?.title || 'Event'}`)}`;
    }

    function buildCalendarUrl(type: 'google' | 'apple' | 'outlook') {
      const title = sticker.value?.title || '';
      const description = sticker.value?.description || '';
      const location = sticker.value?.location || '';
      const date = sticker.value?.eventDate;

      if (type === 'google') {
        addToCalendarClick(date, description, location, title);
        showCalendar.value = false;
        return;
      }

      const startDate = dayjs(date);
      const rawEndDate = sticker.value?.endDate;
      const endDate = rawEndDate && dayjs(rawEndDate).isAfter(startDate)
        ? dayjs(rawEndDate)
        : startDate.add(1, 'hour');
      const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'BEGIN:VEVENT',
        `DTSTART:${startDate.format('YYYYMMDDTHHmmss')}`,
        `DTEND:${endDate.format('YYYYMMDDTHHmmss')}`,
        `SUMMARY:${title}`,
        `DESCRIPTION:${description}`,
        `LOCATION:${location}`,
        'END:VEVENT',
        'END:VCALENDAR',
      ].join('\r\n');

      const blob = new Blob([icsContent], { type: 'text/calendar' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${title.replace(/\s+/g, '-')}.ics`;
      link.click();
      URL.revokeObjectURL(downloadUrl);
      showCalendar.value = false;
    }

    function addToGoogleCalendar() { buildCalendarUrl('google'); }
    function addToAppleCalendar() { buildCalendarUrl('apple'); }
    function addToOutlookCalendar() { buildCalendarUrl('outlook'); }

    return {
      sticker,
      tickets,
      announcements,
      loading,
      announcementsLoading,
      resendLoading,
      confirmationSent,
      showShare,
      showCalendar,
      showAnnouncements,
      showTicketViewer,
      currentTicketIndex,
      ticketsLoading,
      creatorName,
      creatorAvatar,
      creatorInitial,
      hasEventBanner,
      creatorProfilePath,
      handleCreatorClick,
      orderSummary,
      totalCost,
      router,
      formatEventDateTime,
      formatBannerDate,
      formatBannerTime,
      formatPurchaseDate,
      formatRelativeTime,
      ticketQrUrl,
      formatTicketDate,
      formatTicketTime,
      formatDateTime,
      goToViewTickets,
      goToStickerPage,
      getTicketTierName,
      handleResendConfirmation,
      copyUrlToClipboard,
      handleShareTo,
      contactOrganizer,
      addToGoogleCalendar,
      addToAppleCalendar,
      addToOutlookCalendar,
    };
  },
});
</script>

<style lang="scss" scoped>
// ── Page wrapper ──
.ticket-detail-page {
  padding-top: 0 !important;
  padding-left: 24px !important;
  padding-right: 32px !important;
  padding-bottom: 24px !important;
  max-width: 100%;
  margin: 0;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    padding-left: 40px !important;
    padding-right: 48px !important;
    padding-bottom: 32px !important;
  }

  @media (min-width: 1024px) {
    padding-left: 64px !important;
    padding-right: 72px !important;
    padding-bottom: 40px !important;
  }
}

// ── Hero Banner — full-bleed (landscape event banners only) ──
.event-banner {
  margin-left: -24px !important;
  margin-right: -32px !important;
  max-height: 280px;
  overflow: hidden;
  margin-bottom: 24px;
  position: relative;

  @media (min-width: 768px) {
    margin-left: -40px !important;
    margin-right: -48px !important;
  }

  @media (min-width: 1024px) {
    margin-left: -64px !important;
    margin-right: -72px !important;
  }

  .banner-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

// ── Sticker hero — shows full artwork without cropping ──
.sticker-hero {
  margin-left: -24px !important;
  margin-right: -32px !important;
  margin-bottom: 24px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem 2rem;
  min-height: 200px;
  background: linear-gradient(180deg, #f0fdfa 0%, #f9fafb 60%, #fff 100%);

  @media (min-width: 768px) {
    margin-left: -40px !important;
    margin-right: -48px !important;
    padding: 3.5rem 2rem 2.5rem;
    min-height: 260px;
  }

  @media (min-width: 1024px) {
    margin-left: -64px !important;
    margin-right: -72px !important;
    min-height: 300px;
  }
}

.sticker-hero-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 220px;

  @media (min-width: 768px) {
    max-height: 260px;
  }

  @media (min-width: 1024px) {
    max-height: 280px;
  }
}

.sticker-hero-img {
  max-height: 220px;
  max-width: min(320px, 70vw);
  width: auto;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.1));

  @media (min-width: 768px) {
    max-height: 260px;
    max-width: min(360px, 50vw);
  }

  @media (min-width: 1024px) {
    max-height: 280px;
    max-width: min(400px, 40vw);
  }
}

// Banner overlay (date + location) — mirrors StickerDetails
.banner-overlay-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  padding: 10px 16px;

  .overlay-content {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .date-time-info {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;

    .date-text,
    .time-text {
      font-size: 12px;
      font-weight: 500;
      color: white;
    }

    .separator {
      color: rgba(255, 255, 255, 0.6);
      font-size: 12px;
    }
  }

  .location-info {
    flex: 1;
    min-width: 0;

    .location-text {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.9);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: block;
    }
  }
}

.confirmation-toast {
  position: absolute;
  top: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(17, 24, 39, 0.88);
  color: #fff;
  padding: 0.55rem 1.25rem;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.3s ease;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
}

// ── Content body ──
.content-body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

// Campaign Badge
.campaign-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: #ccfbf1;
  color: #0f766e;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 700;
  width: fit-content;
  margin-bottom: 16px;

  .campaign-badge-dot {
    width: 8px;
    height: 8px;
    background: #14b8a6;
    border-radius: 50%;
  }
}

// Creator Info — matches StickerDetails
.creator-info {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  &.is-clickable {
    cursor: pointer;

    &:hover {
      .creator-name {
        color: #0d9488;
      }
    }
  }
}

.creator-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: #0d9488;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .creator-initial {
    color: white;
    font-weight: 700;
    font-size: 20px;
  }
}

.creator-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.creator-name {
  font-size: 14px;
  font-weight: 700;
  color: #1a1a1a;
}

.creator-verified {
  font-size: 12px;
  color: #6b7280;
}

// Title — matches StickerDetails .sticker-title
.sticker-title {
  font-size: 22px;
  font-weight: 900;
  color: #1a1a1a;
  margin: 4px 0 16px;
  line-height: 1.2;

  @media (min-width: 480px) {
    font-size: 26px;
  }

  @media (min-width: 768px) {
    font-size: 30px;
  }
}

// Event meta block (date + location + action buttons)
.event-meta-block {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;

  @media (min-width: 480px) {
    margin-bottom: 24px;
  }
}

.event-meta-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.event-date-row,
.event-location-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.meta-icon {
  width: 0.9rem;
  height: 0.9rem;
  flex-shrink: 0;
  color: #0d9488;
}

.event-date-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0d9488;
}

.location-text {
  font-size: 0.875rem;
  color: #6b7280;

  &.location-link {
    color: #0d9488;
    text-decoration: underline;
    cursor: pointer;
    &:hover { opacity: 0.8; }
  }

  &.location-undisclosed {
    font-style: italic;
  }
}

.location-badge {
  padding: 0.15rem 0.45rem;
  border-radius: 0.2rem;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #fff;
  flex-shrink: 0;

  &.badge-virtual { background: #6b7280; }
  &.badge-physical { background: #0d9488; }
}

// Action icon buttons
.event-action-buttons {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  padding-top: 2px;
}

.action-icon-btn {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover { background: #e5e7eb; }
}

.action-icon {
  width: 1rem;
  height: 1rem;
  opacity: 0.7;
}

// ── Detail sections — flat, not card-based ──
.detail-section {
  padding: 20px 0;
  border-top: 1px solid #f3f4f6;
}

.section-heading {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 16px;
}

// Order summary
.summary-lines {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-tier {
  font-size: 0.875rem;
  color: #6b7280;
}

.summary-price {
  font-size: 0.875rem;
  color: #6b7280;
}

.summary-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 4px 0;
}

.summary-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.summary-total-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
}

.summary-total-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
}

// CTA section (sits between meta block and detail sections)
.cta-section {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding: 20px 0;
  border-top: 1px solid #f3f4f6;

  @media (min-width: 480px) {
    flex-direction: row;
    gap: 0.75rem;
  }
}

.btn-primary {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #0d9488;
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover { opacity: 0.88; }
}

.btn-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.btn-secondary {
  flex: 1;
  padding: 0.75rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 2rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: #e5e7eb; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

// Description HTML
.section-description {
  font-size: 14px;
  color: #374151;
  line-height: 1.65;

  :deep(p) {
    margin: 0 0 0.75rem;
    &:last-child { margin-bottom: 0; }
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 0.75rem;
    padding-left: 1.25rem;
  }

  :deep(li) {
    margin-bottom: 0.25rem;
  }

  :deep(strong),
  :deep(b) {
    font-weight: 600;
    color: #1a1a1a;
  }

  :deep(a) {
    color: #0d9488;
    text-decoration: underline;
  }
}

// Sticker inventory card
.sticker-inventory-card {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 0.75rem;
}

.inventory-thumb {
  width: 3rem;
  height: 3rem;
  border-radius: 0.5rem;
  overflow: hidden;
  flex-shrink: 0;
  background: #e5e7eb;

  .inventory-thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.inventory-info {
  flex: 1;
  min-width: 0;
}

.inventory-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.2rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.inventory-owned {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
}

.btn-outline {
  flex-shrink: 0;
  padding: 0.5rem 0.875rem;
  background: #fff;
  color: #0d9488;
  border: 1.5px solid #0d9488;
  border-radius: 2rem;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;

  &:hover { background: #f0fdfa; }
}

// Purchase date
.purchase-date-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
}

// ── Error State ──
.error-state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;
}

.error-text {
  font-size: 0.9rem;
  color: #6b7280;
  text-align: center;
}

// ── Skeleton Loading ──────────────────────────────────────────────
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@mixin shimmer-bg {
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 6px;
}

.skeleton-banner {
  height: 220px;
  border-radius: 0 !important;
  margin-left: -24px !important;
  margin-right: -32px !important;
  margin-top: 0;
  margin-bottom: 24px;
  background: linear-gradient(180deg, #f0fdfa 0%, #f9fafb 60%, #fff 100%);

  @media (min-width: 768px) {
    height: 260px;
    margin-left: -40px !important;
    margin-right: -48px !important;
  }

  @media (min-width: 1024px) {
    height: 300px;
    margin-left: -64px !important;
    margin-right: -72px !important;
  }

  &::after {
    content: '';
    display: block;
    width: min(280px, 50vw);
    height: min(200px, 70%);
    margin: 2.5rem auto 0;
    @include shimmer-bg;
    border-radius: 12px;
  }
}

.skeleton-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-circle {
  @include shimmer-bg;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  flex-shrink: 0;

  &.skeleton-circle--sm {
    width: 36px;
    height: 36px;
  }
}

.skeleton-creator-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
}

.skeleton-bar {
  @include shimmer-bg;
  height: 14px;

  &.skeleton-bar--short { width: 40%; }
  &.skeleton-bar--medium { width: 60%; }
  &.skeleton-bar--full { width: 100%; }
  &.skeleton-bar--xs { width: 20%; }

  &.skeleton-bar--title {
    height: 32px;
    width: 75%;
    margin-bottom: 4px;
  }

  &.skeleton-bar--section-title {
    height: 18px;
    width: 35%;
    margin-bottom: 8px;
  }

  &.skeleton-bar--meta {
    height: 13px;
    width: 55%;
  }
}

.skeleton-meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.skeleton-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
  margin-bottom: 4px;
}

.skeleton-divider {
  height: 1px;
  background: #f0f0f0;
  margin: 6px 0;
}

.skeleton-summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.skeleton-cta-row {
  display: flex;
  gap: 12px;
}

.skeleton-btn {
  @include shimmer-bg;
  flex: 1;
  height: 44px;
  border-radius: 2rem;
}

// ── Dialog shared styles ──
.dialog-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 0.5rem;
  cursor: pointer;
  border-radius: 0.5rem;
  transition: background 0.15s;

  &:hover { background: #f9fafb; }
}

.dialog-option-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: #0d9488;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 1.125rem;
    height: 1.125rem;
    stroke: #fff;
  }

  img {
    width: 1.125rem;
    height: 1.125rem;
    filter: brightness(0) invert(1);
  }
}

.dialog-option-text {
  font-size: 1rem;
  font-weight: 500;
  color: #0d9488;
}

.dialog-note-section {
  padding-top: 1rem;
  margin-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}

.dialog-note-text {
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0;
}

// ── Ticket Viewer Dialog ──────────────────────────────────────────────

:deep(.ticket-viewer-dialog) {
  .el-dialog__header {
    display: none !important;
    height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    overflow: hidden !important;
  }

  .el-dialog__body {
    padding: 0 !important;
    margin: 0 !important;
    max-height: 80vh;
    overflow-y: auto;
    background: #fff;
  }
}

.ticket-viewer-wrap {
  background: #fff;
  display: flex;
  flex-direction: column;
}

.tv-close-btn {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  border: none;
  cursor: pointer;
  color: #fff;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
}

// Loading
.tv-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 1rem;
  padding: 4rem 1.5rem;
}

.tv-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e0e0e0;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

.tv-loading-text {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
}

// Carousel area
.tv-carousel-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  gap: 1rem;
  overflow-y: auto;
}

.tv-ticket-stage {
  position: relative;
  width: 100%;
  max-width: 100%;
}

// Ticket card
.tv-ticket-card {
  width: 100%;
  background: #fff;
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
  transition: opacity 0.25s ease, transform 0.25s ease;

  &.tv-ticket--hidden {
    display: none;
  }

  &.tv-ticket--active {
    display: block;
  }
}

// Card image
.tv-card-image {
  position: relative;
  height: 220px;
  overflow: hidden;
  background: #e5e7eb;

  .tv-shimmer-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 30%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 70%
    );
    background-size: 200% 100%;
    animation: tvShimmer 3s infinite linear;
    z-index: 1;
    pointer-events: none;
  }

  .tv-card-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    position: relative;
    z-index: 2;
  }

  &.tv-card-image--sticker {
    background: linear-gradient(180deg, #f0fdfa 0%, #f9fafb 100%);

    .tv-card-img {
      object-fit: contain;
      padding: 1rem;
      box-sizing: border-box;
    }
  }
}

@keyframes tvShimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

// Card body
.tv-card-body {
  padding: 1.25rem 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tv-event-name {
  font-size: 1.4rem;
  font-weight: 800;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.2;
}

// Info grid (date/time)
.tv-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.tv-info-cell {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.tv-info-row {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.tv-info-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 600;
  color: #9ca3af;
}

.tv-info-value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1a1a;
}

.tv-option-text {
  font-size: 0.85rem;
  font-weight: 500;
  color: #6b7280;
}

// Seat grid
.tv-seat-grid {
  display: flex;
  justify-content: center;
  gap: 2.5rem;
  padding: 0.75rem 0;
  border-top: 1px solid #f3f4f6;
  border-bottom: 1px solid #f3f4f6;
}

.tv-seat-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
}

.tv-seat-value {
  font-size: 1.75rem;
  font-weight: 900;
  color: #0d9488;
  line-height: 1;
}

.tv-seat-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: #9ca3af;
  text-transform: uppercase;
}

// QR code
.tv-qr-wrap {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;

  :deep(canvas),
  :deep(svg) {
    border-radius: 0.5rem;
  }
}

// Check-in status
.tv-checked-in {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  color: #0d9488;
  font-size: 0.8rem;
  font-weight: 600;

  svg {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }
}

.tv-not-checked-in {
  text-align: center;
  font-size: 0.8rem;
  color: #9ca3af;
}

// Dots
.tv-dots {
  display: flex;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.75rem 0;
}

.tv-dot {
  height: 0.5rem;
  width: 0.5rem;
  border-radius: 9999px;
  background: #d1d5db;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;

  &.tv-dot--active {
    background: #0d9488;
    width: 1.5rem;
  }
}

// Empty
.tv-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.5rem;
  text-align: center;
}

.tv-empty-icon {
  font-size: 3rem;
  margin-bottom: 0.75rem;
  opacity: 0.35;
}

.tv-empty-text {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
}

// ── Announcements ──
.announcements-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  gap: 0.75rem;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e0e0e0;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.announcements-list {
  max-height: 22rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.announcement-item {
  padding: 1rem;
  background: #f9fafb;
  border-radius: 0.5rem;
}

.announcement-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.announcement-author {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
}

.announcement-time {
  font-size: 0.75rem;
  color: #9ca3af;
}

.announcement-body {
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.5;
  margin: 0;
}

.announcements-empty {
  padding: 2rem;
  text-align: center;

  .empty-text {
    font-size: 0.875rem;
    color: #6b7280;
  }
}
</style>

<style lang="scss">
.ticket-viewer-overlay {
  --el-dialog-padding-primary: 0px;

  .ticket-viewer-dialog.el-dialog {
    max-width: 400px !important;
    border-radius: 1rem !important;
    overflow: hidden !important;
    padding: 0 !important;
  }

  .ticket-viewer-dialog .el-dialog__header {
    display: none !important;
    height: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .ticket-viewer-dialog .el-dialog__body {
    padding: 0 !important;
    margin: 0 !important;
    overflow-y: auto !important;
    background: #fff !important;
  }

  .ticket-viewer-dialog .el-dialog__footer {
    padding: 0 !important;
    margin: 0 !important;
  }
}
</style>
